
import React, { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserData } from '@/lib/profile/models/user.model';
import { showDevError } from '@/components/ui/dev-error-toast';
import { AuthContext, type UserRole } from '@/lib/auth/models/auth.model';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isApproved, setIsApproved] = useState(false);
  const [loading, setLoading] = useState(true);

  const initializeAuth = async (session: Session) => {
    if (!session?.user) return;

    try {
      console.log('🔄 Initializing auth');

      const { userData } = await fetchUserData(session.user.id);

      // Apply role chosen before Google OAuth redirect (stored in sessionStorage)
      const pendingJson = sessionStorage.getItem('p2b_google_signup');
      if (pendingJson) {
        sessionStorage.removeItem('p2b_google_signup');
        try {
          const { role: pendingRole, ts } = JSON.parse(pendingJson);
          const isRecent = Date.now() - ts < 10 * 60 * 1000; // 10 min window
          if (isRecent && (pendingRole === 'traveler' || pendingRole === 'travel_designer')) {
            const updates: Record<string, string> = { role: pendingRole };
            if (pendingRole === 'traveler') updates.approval_status = 'approved';
            await supabase.from('users').update(updates).eq('id', session.user.id);
            userData.role = pendingRole;
            if (pendingRole === 'traveler') userData.approval_status = 'approved';
          }
        } catch {
          // malformed sessionStorage value — ignore
        }
      }

      setUserData(userData);
      setIsApproved(userData.approval_status === 'approved');
      setUserRole(userData.role || 'traveler');

    } catch (error) {
      console.error('Error in initial auth check:', error);
      showDevError({ error, context: 'initial auth check' });
      // Set default values on error
      setUserRole('traveler');
      setIsApproved(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (userId: string) => {

    console.log('🔄 Fetching user data for:', userId);

    const { data: user } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    const userData = new UserData(user);

    return { userData, role: userData.role };
  };

  useEffect(() => {

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {

        setTimeout(async () => {

          setSession(session);
          setUser(session?.user ?? null);

          await initializeAuth(session);

          setLoading(false);
        }, 0);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, unknown>) => {
    // Determina il ruolo basato sui metadati
    const role: UserRole = (metadata.role as UserRole) || 'travel_designer';

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          ...metadata,
          role
        }
      }
    });

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (!error) {
      setLoading(true);
    }

    return { error };
  };

  const signOut = async () => {
    try {
      setUserRole(null);
      setIsApproved(false);
      setUser(null);
      setSession(null);
      setUserData(null);
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      showDevError({ error, context: 'sign out' });
      throw error;
    }
  };

  const value = {
    user,
    userData,
    session,
    userRole,
    isApproved,
    signUp,
    signIn,
    signOut,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
